import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Eye, ExternalLink, PauseCircle, PlayCircle, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PROFILE_STATUS_STYLES } from "@/lib/constants";
import { inr } from "@/lib/constants";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

const STATUS_OPTIONS = [
    "draft", "onboarding", "ai_processing", "ai_completed", "admin_review", "changes_requested",
    "admin_approved", "doctor_review", "doctor_approved", "payment_pending", "ready_to_publish",
    "published", "suspended", "archived",
];

export default function AdminDoctors() {
    const [sp, setSp] = useSearchParams();
    const nav = useNavigate();

    const [q, setQ] = useState(sp.get("q") || "");
    const [status, setStatus] = useState(sp.get("status") || "any");
    const [pkg, setPkg] = useState(sp.get("package_code") || "any");
    const [opsBucket, setOpsBucket] = useState(sp.get("ops_bucket") || "");
    const [page, setPage] = useState(parseInt(sp.get("page") || "1", 10));
    const [data, setData] = useState(null);
    const [packages, setPackages] = useState([]);
    const [detailId, setDetailId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    }, []);

    function refetch() {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (status && status !== "any") params.set("status", status);
        if (pkg && pkg !== "any") params.set("package_code", pkg);
        if (opsBucket) params.set("ops_bucket", opsBucket);
        params.set("page", page);
        params.set("page_size", 25);
        api.get("/admin/doctors?" + params.toString())
            .then((r) => setData(r.data))
            .catch((e) => toast.error(errMsg(e, "Failed to load")));
        setSp(params, { replace: true });
    }

    useEffect(() => { refetch(); }, [status, pkg, opsBucket, page]);

    async function openDetail(id) {
        setDetailId(id);
        setDetail(null);
        try {
            const r = await api.get(`/admin/doctors/${id}/detail`);
            setDetail(r.data);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load detail"));
        }
    }

    async function unpublish(id) {
        if (!confirm("Unpublish this doctor's public page?")) return;
        setBusy(true);
        try {
            await api.post(`/admin/publish/${id}/unpublish`, { reason: "admin suspend" });
            toast.success("Unpublished");
            refetch();
            if (detailId === id) openDetail(id);
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        } finally {
            setBusy(false);
        }
    }
    async function republish(id) {
        setBusy(true);
        try {
            await api.post(`/admin/publish/${id}/republish`);
            toast.success("Republished");
            refetch();
            if (detailId === id) openDetail(id);
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        } finally {
            setBusy(false);
        }
    }

    return (
        <AdminShell>
            <div className="mb-6" data-testid="admin-doctors-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Directory</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Doctors</h1>
            </div>

            {/* Filters */}
            <Card className="border-[#123B5D]/10 mb-4">
                <CardContent className="pt-6 flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[220px]">
                        <div className="text-xs text-[#17212B]/60 mb-1">Search (name/specialization/city)</div>
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#123B5D]/50" />
                            <Input
                                value={q} onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && (setPage(1), refetch())}
                                className="pl-9" placeholder="e.g. Sharma, Cardiology, Mumbai"
                                data-testid="doctors-search-input"
                            />
                        </div>
                    </div>
                    <div>
                        <div className="text-xs text-[#17212B]/60 mb-1">Status</div>
                        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                            <SelectTrigger className="w-[180px]" data-testid="doctors-status-filter"><SelectValue placeholder="Any" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">Any</SelectItem>
                                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <div className="text-xs text-[#17212B]/60 mb-1">Package</div>
                        <Select value={pkg} onValueChange={(v) => { setPkg(v); setPage(1); }}>
                            <SelectTrigger className="w-[180px]" data-testid="doctors-package-filter"><SelectValue placeholder="Any" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">Any</SelectItem>
                                {packages.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {opsBucket && (
                        <div className="flex items-center gap-2 text-xs bg-[#0F766E]/10 text-[#0F766E] rounded-full px-3 py-1.5">
                            Ops: {opsBucket}
                            <button onClick={() => { setOpsBucket(""); setPage(1); }} data-testid="doctors-clear-ops">
                                <X size={12} />
                            </button>
                        </div>
                    )}
                    <Button onClick={() => { setPage(1); refetch(); }} data-testid="doctors-apply-filters">Apply</Button>
                </CardContent>
            </Card>

            {/* Table (desktop) + Card list (mobile) */}
            <Card className="border-[#123B5D]/10">
                <CardContent className="pt-4">
                    {/* Mobile card list — visible on <md */}
                    <div className="md:hidden space-y-3" data-testid="doctors-cards-mobile">
                        {(data?.items || []).map((r) => {
                            const psty = PROFILE_STATUS_STYLES[r.profile_status] || PROFILE_STATUS_STYLES.draft;
                            return (
                                <div key={r.doctor_id} className="rounded-lg border border-[#E5E9F2] p-3" data-testid={`doctor-card-${r.doctor_id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-semibold text-[#123B5D] truncate">{r.full_name}</div>
                                            <div className="text-[11px] text-[#64748B] truncate">{r.email || "—"}</div>
                                            <div className="text-[11px] text-[#64748B] mt-0.5">{r.specialization} · {r.city}</div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            <span className={"ddi-badge text-[10px] " + psty.cls}>{psty.label}</span>
                                            <span className="ddi-badge bg-slate-100 text-slate-700 text-[10px]">{r.payment_state}</span>
                                        </div>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-[#E5E9F2] flex items-center justify-between text-[11px] text-[#64748B]">
                                        <div>{r.package_code || "—"} · AI: {r.ai_status || "—"}</div>
                                        <div className="flex gap-1">
                                            <Button size="sm" variant="ghost" onClick={() => openDetail(r.doctor_id)} data-testid={`view-mobile-${r.doctor_id}`}><Eye size={14} /></Button>
                                            {r.slug && r.profile_status === "published" && (
                                                <a href={`/api/public/dr/${r.slug}`} target="_blank" rel="noopener"><Button size="sm" variant="ghost"><ExternalLink size={14} /></Button></a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {/* Desktop table — hidden on <md */}
                    <div className="hidden md:block overflow-x-auto">
                        <Table data-testid="doctors-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Doctor</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Specialty · City</TableHead>
                                <TableHead>Package</TableHead>
                                <TableHead>Payment</TableHead>
                                <TableHead>Profile</TableHead>
                                <TableHead>AI</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead>Last login</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(data?.items || []).map((r) => {
                                const psty = PROFILE_STATUS_STYLES[r.profile_status] || PROFILE_STATUS_STYLES.draft;
                                return (
                                    <TableRow key={r.doctor_id} data-testid={`doctor-row-${r.doctor_id}`}>
                                        <TableCell className="font-semibold text-[#123B5D]">{r.full_name}</TableCell>
                                        <TableCell className="text-xs">{r.email || "—"}</TableCell>
                                        <TableCell className="text-xs">{r.specialization} · {r.city}</TableCell>
                                        <TableCell className="text-xs">{r.package_code || "—"}</TableCell>
                                        <TableCell><span className="ddi-badge bg-slate-100 text-slate-700 text-[10px]">{r.payment_state}</span></TableCell>
                                        <TableCell><span className={"ddi-badge text-[10px] " + psty.cls}>{psty.label}</span></TableCell>
                                        <TableCell className="text-xs">{r.ai_status || "—"}</TableCell>
                                        <TableCell className="text-xs">{r.created_at?.slice(0, 10)}</TableCell>
                                        <TableCell className="text-xs">{r.last_login_at?.slice(0, 10) || "—"}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="inline-flex gap-1">
                                                <Button size="sm" variant="ghost" onClick={() => openDetail(r.doctor_id)} data-testid={`view-${r.doctor_id}`}><Eye size={14} /></Button>
                                                {r.slug && r.profile_status === "published" && (
                                                    <>
                                                        <a href={`/api/public/dr/${r.slug}`} target="_blank" rel="noopener">
                                                            <Button size="sm" variant="ghost"><ExternalLink size={14} /></Button>
                                                        </a>
                                                        <Button size="sm" variant="ghost" onClick={() => unpublish(r.doctor_id)} data-testid={`unpublish-${r.doctor_id}`}><PauseCircle size={14} /></Button>
                                                    </>
                                                )}
                                                {r.profile_status === "suspended" && (
                                                    <Button size="sm" variant="ghost" onClick={() => republish(r.doctor_id)} data-testid={`republish-${r.doctor_id}`}><PlayCircle size={14} /></Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {(!data || data.items.length === 0) && (
                                <TableRow><TableCell colSpan={10} className="text-center text-[#17212B]/60 py-6">No doctors match those filters.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </div>
                    {data && data.total > (data.items?.length || 0) && (
                        <div className="flex items-center justify-between p-3 text-xs text-[#17212B]/60">
                            <div>Showing {data.items.length} of {data.total}</div>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="doctors-prev">Prev</Button>
                                <div className="self-center">Page {page}</div>
                                <Button size="sm" variant="outline" disabled={page * 25 >= data.total} onClick={() => setPage((p) => p + 1)} data-testid="doctors-next">Next</Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Detail drawer */}
            <Sheet open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}>
                <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="doctor-detail-drawer">
                    <SheetHeader>
                        <SheetTitle>Doctor detail</SheetTitle>
                    </SheetHeader>
                    {!detail ? (
                        <div className="mt-4 text-[#17212B]/60 text-sm">Loading…</div>
                    ) : (
                        <div className="mt-4 space-y-6">
                            <section>
                                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60 mb-2">Doctor</div>
                                <div className="text-xl font-bold text-[#123B5D]">{detail.doctor.full_name}</div>
                                <div className="text-sm text-[#17212B]/70">{detail.doctor.specialization} · {detail.doctor.city} · {detail.doctor.medical_registration_number}</div>
                                <div className="text-sm text-[#17212B]/70">{detail.user?.email}</div>
                                <div className="mt-2 flex gap-2 flex-wrap">
                                    <Button size="sm" onClick={() => nav(`/admin/review/${detailId}`)} data-testid="detail-review-cta">Open review workspace</Button>
                                    <Button size="sm" variant="outline" onClick={() => nav(`/admin/doctors/${detailId}/reviews`)} data-testid="detail-audit-tools-cta">Audit tools</Button>
                                    {detail.doctor.slug && (
                                        <a href={`/api/public/dr/${detail.doctor.slug}`} target="_blank" rel="noopener">
                                            <Button size="sm" variant="outline">Preview public page</Button>
                                        </a>
                                    )}
                                </div>
                            </section>
                            <section className="grid grid-cols-2 gap-3">
                                <div><div className="text-xs text-[#17212B]/60">Status</div><div className="font-semibold">{detail.doctor.profile_status}</div></div>
                                <div><div className="text-xs text-[#17212B]/60">Payment</div><div className="font-semibold">{detail.doctor.payment_state}</div></div>
                                <div><div className="text-xs text-[#17212B]/60">Package</div><div className="font-semibold">{detail.doctor.package_code || "—"}</div></div>
                                <div><div className="text-xs text-[#17212B]/60">AI</div><div className="font-semibold">{detail.ai_job?.status || "—"}</div></div>
                            </section>
                            <section>
                                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60 mb-2">Payments</div>
                                <ul className="text-sm divide-y">
                                    {(detail.payments || []).slice(0, 10).map((p) => (
                                        <li key={p.id} className="py-2 flex justify-between">
                                            <span>{p.gateway_payment_id?.slice(0, 12) || p.id?.slice(0, 8)} · {p.status}</span>
                                            <span className="font-semibold">{inr(p.amount_inr)}</span>
                                        </li>
                                    ))}
                                    {(detail.payments || []).length === 0 && <li className="py-2 text-[#17212B]/50">No payments yet.</li>}
                                </ul>
                            </section>
                            <section>
                                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60 mb-2">Approval history</div>
                                <ul className="text-sm divide-y">
                                    {(detail.history || []).map((h) => (
                                        <li key={h.id} className="py-2 flex justify-between">
                                            <span>{h.action} <span className="text-[#17212B]/50 ml-2">by {h.actor_role}</span></span>
                                            <span className="text-[#17212B]/50">{h.created_at?.slice(0, 10)}</span>
                                        </li>
                                    ))}
                                    {(detail.history || []).length === 0 && <li className="py-2 text-[#17212B]/50">No history yet.</li>}
                                </ul>
                            </section>
                            <section>
                                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60 mb-2">Publish versions</div>
                                <ul className="text-sm divide-y">
                                    {(detail.versions || []).slice(0, 10).map((v) => (
                                        <li key={v.id} className="py-2 flex justify-between">
                                            <span>v{v.version_number} · {v.template_code}</span>
                                            <span className="text-[#17212B]/50">{v.published_at?.slice(0, 10)}</span>
                                        </li>
                                    ))}
                                    {(detail.versions || []).length === 0 && <li className="py-2 text-[#17212B]/50">Not published yet.</li>}
                                </ul>
                            </section>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </AdminShell>
    );
}
